import React, {CSSProperties, useEffect, useImperativeHandle} from "react";
import {Button, Modal, ModalHeader, ModalBody, ModalFooter} from "reactstrap";
import {
    addDays,
    endOfDay,
    startOfDay,
    startOfMonth,
    endOfMonth,
    addMonths,
    startOfWeek,
    endOfWeek,
    isSameDay,
    differenceInCalendarDays,
    startOfYear,
    format,
    startOfQuarter,
    endOfQuarter,
    endOfYear,
} from "date-fns";
import {DateRangePicker } from "react-date-range";

interface Props {
    id: string;
    onChangeValue: (name, value) => void;
    text?: { // starting
        data: {
            minDate: string;
            maxDate: string;
        };
        label: string;
    };
    value?: { // controlled value
        data: {
            minDate: string;
            maxDate: string;
        };
        label: string;
    } | null;
    placeholder?: string;
    maxPastDays?: number;
    maxFutureDays?: number;
    inputStyle?: CSSProperties;
    defaultIsThisMonth?: boolean;
    startOfInputLabel?: string;
    title?: string;
    hideInput?: boolean
}

export interface DateRangeRef {
    openDateRangeModal: () => void;
}

const FilterCustomDateRange = React.forwardRef((props: Props, ref) => {
    const {
        id,
        onChangeValue,
        text,
        value,
        inputStyle,
        placeholder = 'Select date range',
        maxPastDays,
        maxFutureDays,
        defaultIsThisMonth = false,
        startOfInputLabel = '',
        title,
        hideInput = false,
    } = props;
    const defineds = {
        startOfWeek: startOfWeek(new Date()),
        endOfWeek: endOfWeek(new Date()),
        startOfLastWeek: startOfWeek(addDays(new Date(), -7)),
        endOfLastWeek: endOfWeek(addDays(new Date(), -7)),
        startOfToday: startOfDay(new Date()),
        endOfToday: endOfDay(new Date()),
        startOfYesterday: startOfDay(addDays(new Date(), -1)),
        endOfYesterday: endOfDay(addDays(new Date(), -1)),
        startOfMonth: startOfMonth(new Date()),
        endOfMonth: endOfMonth(new Date()),
        startOfLastMonth: startOfMonth(addMonths(new Date(), -1)),
        endOfLastMonth: endOfMonth(addMonths(new Date(), -1)),
        startOfNextMonth: startOfMonth(addMonths(new Date(), +1)),
        endOfNextMonth: endOfMonth(addMonths(new Date(), +1)),
        startOfQuarter: startOfQuarter(new Date()),
        endOfQuarter: endOfQuarter(new Date()),
        startOfLastQuarter: startOfQuarter(addMonths(new Date(), -3)),
        endOfLastQuarter: endOfQuarter(addMonths(new Date(), -3)),
        startOfNextQuarter: startOfQuarter(addMonths(new Date(), +3)),
        endOfNextQuarter: endOfQuarter(addMonths(new Date(), +3)),
        startOfYear: startOfYear(new Date()),
        endOfYear: endOfYear(new Date()),
    };

    const [state, setState] = React.useState([
        {
            startDate: text ? new Date(text?.data?.minDate) : (defaultIsThisMonth ? defineds.startOfMonth : defineds.startOfToday),
            endDate: text ? new Date(text?.data?.maxDate) : (defaultIsThisMonth ? defineds.endOfMonth : defineds.endOfToday),
            key: "selection",
            label: text ? text?.label : (defaultIsThisMonth ? "This Month" : ""),
        },
    ]);

    const [input, setInput] = React.useState<string>(text ? text?.label : (defaultIsThisMonth ? "This Month" : ""));

    useEffect(() => {
        if (value || value === null) {
            setState([
                {
                    startDate: value ? new Date(value?.data?.minDate) : (defaultIsThisMonth ? defineds.startOfMonth : defineds.startOfToday),
                    endDate: value ? new Date(value?.data?.maxDate) : (defaultIsThisMonth ? defineds.endOfMonth : defineds.endOfToday),
                    key: "selection",
                    label: value ? value?.label : (defaultIsThisMonth ? "This Month" : ""),
                },
            ])
            setInput(value ? value?.label : (defaultIsThisMonth ? "This Month" : ""))
        }
    }, [value]);

    const [isOpenModal, setIsOpenModal] = React.useState(false);

    useImperativeHandle(ref, () => ({
        openDateRangeModal() {
            setIsOpenModal(true)
        }
    }));

    const today = new Date();
    const lastYear = new Date(
        today.getFullYear() - 1,
        today.getMonth(),
        today.getDate() + 1
    );
    const nextYear = new Date(
        today.getFullYear() + 1,
        today.getMonth(),
        today.getDate() + 1
    );

    function getDaysFromToday(endDate) {
        return differenceInCalendarDays(defineds.startOfToday, endDate);
    }

    const pastRanges = [
        ...(maxPastDays && !(maxPastDays >= 0) ? [] : [
            {
                label: "Today",
                range: () => ({
                    startDate: defineds.startOfToday,
                    endDate: defineds.endOfToday,
                    label: "Today",
                }),
                isSelected(range) {
                    const definedRange = this.range();
                    return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                    );
                },
            },
        ]),
        ...(maxPastDays && !(maxPastDays >= 1) ? [] : [
            {
                label: "Yesterday",
                range: () => ({
                    startDate: defineds.startOfYesterday,
                    endDate: defineds.endOfYesterday,
                    label: "Yesterday",
                }),
                isSelected(range) {
                    const definedRange = this.range();
                    return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                    );
                },
            },
        ]),
        ...(maxPastDays && !(maxPastDays >= getDaysFromToday(defineds.startOfWeek)) ? [] : [
            {
                label: "This Week",
                range: () => ({
                    startDate: defineds.startOfWeek,
                    endDate: defineds.endOfWeek,
                    label: "This Week",
                }),
                isSelected(range) {
                    const definedRange = this.range();
                    return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                    );
                },
            },
        ]),
        ...(maxPastDays && !(maxPastDays >= getDaysFromToday(defineds.startOfLastWeek)) ? [] : [
            {
                label: "Last Week",
                range: () => ({
                    startDate: defineds.startOfLastWeek,
                    endDate: defineds.endOfLastWeek,
                    label: "Last Week",
                }),
                isSelected(range) {
                    const definedRange = this.range();
                    return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                    );
                },
            },
        ]),
        ...(maxPastDays && !(maxPastDays >= getDaysFromToday(defineds.startOfMonth)) ? [] : [
            {
                label: "This Month",
                range: () => ({
                    startDate: defineds.startOfMonth,
                    endDate: defineds.endOfMonth,
                    label: "This Month",
                }),
                isSelected(range) {
                    const definedRange = this.range();
                    return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                    );
                },
            },
        ]),
        ...(maxPastDays && !(maxPastDays >= getDaysFromToday(defineds.startOfLastMonth)) ? [] : [
            {
                label: "Last Month",
                range: () => ({
                    startDate: defineds.startOfLastMonth,
                    endDate: defineds.endOfLastMonth,
                    label: "Last Month",
                }),
                isSelected(range) {
                    const definedRange = this.range();
                    return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                    );
                },
            },
        ]),
        ...(maxPastDays && !(maxPastDays >= getDaysFromToday(startOfYear(new Date()))) ? [] : [
            {
                label: "This Year",
                range: () => ({
                    startDate: startOfYear(new Date()),
                    endDate: endOfDay(new Date()),
                    label: "This Year",
                }),
                isSelected(range) {
                    const definedRange = this.range();
                    return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                    );
                },
            },
        ]),
        ...(maxPastDays && !(maxPastDays >= getDaysFromToday(lastYear)) ? [] : [
            {
                label: "Last Year",
                range: () => ({
                    startDate: lastYear,
                    endDate: today,
                    label: "Last Year",
                }),
                isSelected(range) {
                    const definedRange = this.range();
                    return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                    );
                },
            },
        ]),
    ];

    const futureRanges = [
        ...(maxFutureDays && !(maxFutureDays >= 0) ? [] : [
            {
                label: "This Month",
                range: () => ({
                    startDate: defineds.startOfMonth,
                    endDate: defineds.endOfMonth,
                    label: "This Month",
                }),
                isSelected(range) {
                    const definedRange = this.range();
                    return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                    );
                },
            },
        ]),
        ...(maxFutureDays && !(maxFutureDays >= getDaysFromToday(defineds.startOfQuarter)) ? [] : [
            {
                label: "This Quarter",
                range: () => ({
                    startDate: defineds.startOfQuarter,
                    endDate: defineds.endOfQuarter,
                    label: "This Quarter",
                }),
                isSelected(range) {
                    const definedRange = this.range();
                    return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                    );
                },
            },
        ]),
        ...(maxFutureDays && !(maxFutureDays >= getDaysFromToday(defineds.startOfNextMonth)) ? [] : [
            {
                label: "Next Month",
                range: () => ({
                    startDate: defineds.startOfNextMonth,
                    endDate: defineds.endOfNextMonth,
                    label: "Next Month",
                }),
                isSelected(range) {
                    const definedRange = this.range();
                    return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                    );
                },
            },
        ]),
        ...(maxFutureDays && !(maxFutureDays >= getDaysFromToday(defineds.startOfNextQuarter)) ? [] : [
            {
                label: "Next Quarter",
                range: () => ({
                    startDate: defineds.startOfNextQuarter,
                    endDate: defineds.endOfNextQuarter,
                    label: "Next Quarter",
                }),
                isSelected(range) {
                    const definedRange = this.range();
                    return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                    );
                },
            },
        ]),
        ...(maxFutureDays && !(maxFutureDays >= getDaysFromToday(defineds.startOfLastMonth)) ? [] : [
            {
                label: "Last Month",
                range: () => ({
                    startDate: defineds.startOfLastMonth,
                    endDate: defineds.endOfLastMonth,
                    label: "Last Month",
                }),
                isSelected(range) {
                    const definedRange = this.range();
                    return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                    );
                },
            },
        ]),
        ...(maxFutureDays && !(maxFutureDays >= getDaysFromToday(defineds.startOfLastQuarter)) ? [] : [
            {
                label: "Last Quarter",
                range: () => ({
                    startDate: defineds.startOfLastQuarter,
                    endDate: defineds.endOfLastQuarter,
                    label: "Last Quarter",
                }),
                isSelected(range) {
                    const definedRange = this.range();
                    return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                    );
                },
            },
        ]),
        ...(maxFutureDays && !(maxFutureDays >= getDaysFromToday(startOfYear(new Date()))) ? [] : [
            {
                label: "This Year",
                range: () => ({
                    startDate: startOfYear(new Date()),
                    endDate: endOfYear(new Date()),
                    label: "This Year",
                }),
                isSelected(range) {
                    const definedRange = this.range();
                    return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                    );
                },
            },
        ]),
        ...(maxFutureDays && !(maxFutureDays >= getDaysFromToday(lastYear)) ? [] : [
            {
                label: "Last Year",
                range: () => ({
                    startDate: lastYear,
                    endDate: today,
                    label: "Last Year",
                }),
                isSelected(range) {
                    const definedRange = this.range();
                    return (
                        isSameDay(range.startDate, definedRange.startDate) &&
                        isSameDay(range.endDate, definedRange.endDate)
                    );
                },
            },
        ]),
    ];

    const staticRanges = maxFutureDays ? futureRanges : pastRanges;
    const inputRanges = [
        {
            label: "days up to today",
            range(value) {
                const minimumDays = Math.min(Math.max(Number(value), 1));
                const daysUpToToday = maxPastDays ? Math.min(minimumDays, maxPastDays) : minimumDays;
                return {
                    startDate: addDays(
                        defineds.startOfToday,
                        (daysUpToToday - 1) * -1
                    ),
                    endDate: defineds.endOfToday,
                    label: `${value} days up to today`,
                };
            },
            getCurrentValue(range) {
                if (!isSameDay(range.endDate, defineds.endOfToday)) return "-";
                if (!range.startDate) return "∞";
                return (
                    differenceInCalendarDays(defineds.endOfToday, range.startDate) + 1
                );
            },
        },
        ...(typeof maxFutureDays !== 'undefined' && maxFutureDays >= 0 ? [
            {
                label: "days from today",
                range(value) {
                    const minimumDays = Math.max(Number(value), 1);
                    const daysFromToday = maxFutureDays
                        ? Math.min(minimumDays, maxFutureDays)
                        : minimumDays;
                    return {
                        startDate: defineds.startOfToday,
                        endDate: addDays(
                            defineds.startOfToday,
                            daysFromToday - 1
                        ),
                        label: `${value} days from today`,
                    };
                },
                getCurrentValue(range) {
                    if (!isSameDay(range.startDate, defineds.startOfToday)) return "-";
                    if (!range.endDate) return "∞";
                    const dayDifference = differenceInCalendarDays(range.endDate, defineds.startOfToday) + 1;
                    return dayDifference > 0 ? dayDifference : "-";
                },
            },
        ] : [])
    ];


    const handleChangesApplied = () => {
        const {startDate, endDate, label} = state[0];
        setInput(label);
        setIsOpenModal(false);

        // Capture user's timezone offset in minutes
        const timezoneOffset = new Date().getTimezoneOffset();

        // Convert startDate and endDate to the user's local time
        const adjustedStartDate = new Date(startDate.getTime() + timezoneOffset * 60 * 1000);
        const adjustedEndDate = new Date(endDate.getTime() + timezoneOffset * 60 * 1000);

        onChangeValue(id, {
            value: label,
            label,
            data: {
                minDate: adjustedStartDate.toISOString(),
                maxDate: adjustedEndDate.toISOString(),
            },
        });
    };

    const toggle = () => {
        setIsOpenModal((prevState) => !prevState);
    };

    const clearSelection = () => {
        setInput((defaultIsThisMonth ? "This Month" : ""));
        setState([
            {
                startDate: (defaultIsThisMonth ? defineds.startOfMonth : defineds.startOfToday),
                endDate: (defaultIsThisMonth ? defineds.endOfMonth : defineds.endOfToday),
                key: "selection",
                label: (defaultIsThisMonth ? "This Month" : ""),
            },
        ]);
    };

    const handleDateRangePickerChange = (item) => {
        const {startDate, endDate} = item.selection;
        const staticRange = staticRanges.find((sr) => {
            const range = sr.range();
            return (
                range.startDate.getTime() - startDate.getTime() === 0 &&
                range.endDate.getTime() - endDate.getTime() === 0
            );
        });
        item.selection.label = staticRange
            ? staticRange.label
            : format(item.selection.startDate, "MM/dd/yyy") +
            " - " +
            format(item.selection.endDate, "MM/dd/yyyy");
        setState([item.selection]);
    };

    const minDate = maxPastDays ? addDays(defineds.startOfToday, (maxPastDays - 1) * -1) : undefined;
    const maxDate = maxFutureDays ? addDays(defineds.endOfToday, (maxFutureDays)) : new Date();

    return (
        <div>
            { !hideInput &&
                <input
                    type="text"
                    readOnly
                    onFocus={toggle}
                    placeholder={placeholder}
                    autoFocus={false}
                    value={startOfInputLabel + input}
                    className="date-range-picker"
                    style={inputStyle}
                />}
            <Modal
                isOpen={isOpenModal}
                toggle={toggle}
                size="xl"
                returnFocusAfterClose={false}
                className="genai-date-range-modal"
            >
                {title && (
                    <ModalHeader toggle={toggle}>
                        <span>{title}</span>
                    </ModalHeader>
                )}
                <ModalBody className="p-0">
                    <style>
                        {`
                        .genai-date-range-modal .modal-content {
                            border-radius: var(--rf-radius-lg, 16px) !important;
                            border: 1px solid var(--rf-border, #E5E7EB) !important;
                            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
                            padding-top: 0;
                            overflow: hidden;
                        }

                        .genai-date-range-modal .modal-header {
                            padding: 20px 24px !important;
                            border-bottom: 1px solid var(--rf-border, #E5E7EB) !important;
                            margin-bottom: 0;
                        }

                        .genai-date-range-modal .modal-header .btn-close {
                            padding: 8px;
                            margin: 0;
                            position: absolute;
                            right: 24px;
                            top: 20px;
                        }

                        .genai-date-range-modal .modal-body {
                            padding: 0 !important;
                        }

                        /* ===== Layout ===== */
                        .genai-date-range-modal .rdrDateRangePickerWrapper {
                            display: flex;
                        }

                        .genai-date-range-modal .rdrCalendarWrapper {
                            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                            background-color: var(--rf-surface, #FFFFFF);
                            flex: 1;
                        }

                        /* ===== Sidebar ===== */
                        .genai-date-range-modal .rdrDefinedRangesWrapper {
                            border-right: 1px solid var(--rf-border, #E5E7EB);
                            background: var(--rf-bg, #FAFAFA);
                            width: 180px;
                            min-width: 180px;
                            padding: 0;
                            display: flex;
                            flex-direction: column;
                        }

                        .genai-date-range-modal .rdrStaticRanges {
                            flex: 1;
                            display: flex;
                            flex-direction: column;
                            padding: 8px;
                        }

                        .genai-date-range-modal .rdrStaticRange {
                            font-size: 0.875rem;
                            color: var(--rf-text-primary, #111827);
                            padding: 0;
                            border-bottom: none;
                            background: transparent;
                            flex: 1;
                            display: flex;
                            align-items: stretch;
                        }

                        .genai-date-range-modal .rdrStaticRangeLabel {
                            font-weight: 500;
                            padding: 0 16px;
                            border-radius: var(--rf-radius-sm, 6px);
                            transition: all 0.15s ease;
                            line-height: 1;
                            display: flex;
                            align-items: center;
                            width: 100%;
                        }

                        .genai-date-range-modal .rdrStaticRange:hover .rdrStaticRangeLabel,
                        .genai-date-range-modal .rdrStaticRange:focus .rdrStaticRangeLabel {
                            background: var(--rf-primary-light, #EEF2FF);
                            color: var(--rf-primary, #4F46E5);
                        }

                        .genai-date-range-modal .rdrStaticRangeSelected .rdrStaticRangeLabel {
                            color: var(--rf-primary, #4F46E5);
                            font-weight: 600;
                            background: var(--rf-primary-light, #EEF2FF);
                        }

                        /* Input ranges (days up to today) */
                        .genai-date-range-modal .rdrInputRanges {
                            border-top: 1px solid var(--rf-border, #E5E7EB);
                            padding: 16px 12px;
                        }

                        .genai-date-range-modal .rdrInputRange {
                            padding: 4px 0;
                            font-size: 0.8125rem;
                            color: var(--rf-text-secondary, #6B7280);
                        }

                        .genai-date-range-modal .rdrInputRange input {
                            font-size: 0.8125rem;
                            color: var(--rf-text-primary, #111827);
                            padding: 6px 8px;
                            border: 1px solid var(--rf-border, #E5E7EB);
                            border-radius: var(--rf-radius-sm, 6px);
                            width: 44px;
                            text-align: center;
                            transition: border-color 0.15s ease;
                        }

                        .genai-date-range-modal .rdrInputRange input:focus {
                            outline: none;
                            border-color: var(--rf-primary, #4F46E5);
                            box-shadow: 0 0 0 1px var(--rf-primary, #4F46E5);
                        }

                        /* ===== Date inputs at top ===== */
                        .genai-date-range-modal .rdrDateDisplayWrapper {
                            background: var(--rf-bg, #FAFAFA);
                            border-bottom: 1px solid var(--rf-border, #E5E7EB);
                            padding: 16px 24px;
                        }

                        .genai-date-range-modal .rdrDateDisplay {
                            margin: 0;
                            gap: 12px;
                        }

                        .genai-date-range-modal .rdrDateDisplayItem {
                            border-radius: var(--rf-radius-md, 12px);
                            border: 1px solid var(--rf-border, #E5E7EB);
                            background: var(--rf-surface, #FFFFFF);
                            box-shadow: none;
                        }

                        .genai-date-range-modal .rdrDateDisplayItem input {
                            color: var(--rf-text-primary, #111827);
                            font-size: 0.875rem;
                            font-weight: 500;
                            height: 44px;
                        }

                        .genai-date-range-modal .rdrDateDisplayItemActive {
                            border-color: var(--rf-primary, #4F46E5);
                            box-shadow: 0 0 0 1px var(--rf-primary, #4F46E5);
                        }

                        /* ===== Calendar area ===== */
                        .genai-date-range-modal .rdrMonths {
                            gap: 40px;
                            padding: 0 32px 24px;
                            justify-content: center;
                        }

                        .genai-date-range-modal .rdrMonth {
                            padding: 0;
                            width: 280px;
                        }

                        /* Month/Year navigation */
                        .genai-date-range-modal .rdrMonthAndYearWrapper {
                            padding: 16px 32px 8px;
                            height: auto;
                        }

                        .genai-date-range-modal .rdrMonthAndYearPickers {
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            padding: 0;
                            gap: 0.5rem;
                        }

                        .genai-date-range-modal .rdrMonthAndYearPickers select {
                            font-size: 0.9375rem;
                            font-weight: 600;
                            color: var(--rf-text-primary, #111827);
                            padding: 0.375rem 1.5rem 0.375rem 0.5rem;
                            border: none;
                            background-color: transparent;
                            cursor: pointer;
                            appearance: none;
                            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath fill='%236B7280' d='M5 7.5L1 3.5h8z'/%3E%3C/svg%3E");
                            background-repeat: no-repeat;
                            background-position: right 0.25rem center;
                            background-size: 10px;
                        }

                        .genai-date-range-modal .rdrMonthAndYearPickers select:focus {
                            outline: none;
                        }

                        /* Navigation arrows */
                        .genai-date-range-modal .rdrNextPrevButton {
                            background: var(--rf-surface, #FFFFFF);
                            border: 1px solid var(--rf-border, #E5E7EB);
                            border-radius: var(--rf-radius-sm, 6px);
                            width: 32px;
                            height: 32px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            transition: all 0.15s ease;
                            margin: 0 4px;
                        }

                        .genai-date-range-modal .rdrNextPrevButton:hover {
                            background: var(--rf-primary-light, #EEF2FF);
                            border-color: var(--rf-primary, #4F46E5);
                        }

                        .genai-date-range-modal .rdrPprevButton i {
                            border-width: 5px 6px 5px 5px;
                            border-color: transparent var(--rf-text-secondary, #6B7280) transparent transparent;
                            transform: translate(-2px, 0);
                        }

                        .genai-date-range-modal .rdrNextButton i {
                            border-width: 5px 5px 5px 6px;
                            border-color: transparent transparent transparent var(--rf-text-secondary, #6B7280);
                            transform: translate(2px, 0);
                        }

                        /* Month name */
                        .genai-date-range-modal .rdrMonthName {
                            font-size: 0.9375rem;
                            font-weight: 600;
                            color: var(--rf-text-primary, #111827);
                            padding: 0.5rem 0 0.75rem;
                        }

                        /* Weekday headers */
                        .genai-date-range-modal .rdrWeekDay {
                            font-size: 0.75rem;
                            font-weight: 600;
                            color: var(--rf-text-tertiary, #9CA3AF);
                            text-transform: uppercase;
                            line-height: 36px;
                        }

                        /* Day cells */
                        .genai-date-range-modal .rdrDay {
                            height: 36px;
                            line-height: 36px;
                        }

                        .genai-date-range-modal .rdrDayNumber span {
                            color: var(--rf-text-primary, #111827);
                            font-weight: 400;
                            font-size: 0.875rem;
                        }

                        .genai-date-range-modal .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span,
                        .genai-date-range-modal .rdrDay:not(.rdrDayPassive) .rdrDayInPreview ~ .rdrDayNumber span {
                            color: var(--rf-text-primary, #111827) !important;
                        }

                        .genai-date-range-modal .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span,
                        .genai-date-range-modal .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span {
                            color: #FFFFFF !important;
                        }

                        .genai-date-range-modal .rdrDayPassive .rdrDayNumber span {
                            color: var(--rf-text-tertiary, #9CA3AF);
                        }

                        .genai-date-range-modal .rdrDayToday .rdrDayNumber span {
                            font-weight: 700;
                        }

                        .genai-date-range-modal .rdrDayToday .rdrDayNumber span:after {
                            background: var(--rf-primary, #4F46E5);
                            width: 20px;
                            height: 2px;
                            bottom: 2px;
                            border-radius: 1px;
                        }

                        /* Selected range colors */
                        .genai-date-range-modal .rdrStartEdge,
                        .genai-date-range-modal .rdrEndEdge {
                            background: var(--rf-primary, #4F46E5) !important;
                            border-radius: 8px;
                        }

                        .genai-date-range-modal .rdrInRange {
                            background: var(--rf-primary-light, #EEF2FF) !important;
                        }

                        .genai-date-range-modal .rdrDayStartPreview,
                        .genai-date-range-modal .rdrDayEndPreview {
                            border-color: var(--rf-primary, #4F46E5);
                        }

                        .genai-date-range-modal .rdrDayInPreview {
                            border-color: var(--rf-primary, #4F46E5);
                        }

                        .genai-date-range-modal .rdrDay:not(.rdrDayPassive) .rdrDayNumber span:hover {
                            color: var(--rf-primary, #4F46E5);
                        }

                        /* ===== Footer buttons ===== */
                        .genai-date-range-modal .button-container {
                            position: relative;
                            padding: 20px 32px;
                            display: flex;
                            justify-content: flex-end;
                            gap: 10px;
                            background: var(--rf-bg, #FAFAFA);
                            border-top: 1px solid var(--rf-border, #E5E7EB);
                        }

                        .genai-date-range-modal .modal-header h5 {
                            font-size: 1.125rem;
                            font-weight: 600;
                            color: var(--rf-text-primary, #111827);
                            margin: 0;
                        }
                        `}
                    </style>
                    <DateRangePicker
                        onChange={handleDateRangePickerChange}
                        showPreview={true}
                        moveRangeOnFirstSelection={false}
                        ranges={state}
                        months={2}
                        direction="horizontal"
                        staticRanges={staticRanges}
                        inputRanges={inputRanges}
                        minDate={minDate}
                        maxDate={maxDate}
                    />
                    <div className="button-container">
                    <Button className={"btn-typography-secondary"} onClick={clearSelection}>
                        Clear
                    </Button>
                    <Button className={"btn-typography-primary"} onClick={handleChangesApplied}>
                        Select
                    </Button>
                    <Button className={"btn-typography-secondary"} onClick={toggle}>
                        Cancel
                    </Button>
                    </div>
                </ModalBody>
            </Modal>
        </div>
    );
});

export default FilterCustomDateRange;